/**
 * Pure AU date-entry helpers shared by DateEntryInput.
 * No React / CSS deps — safe to unit-test from any package.
 */

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
/** AU day/month with 2- or 4-digit year, separators / . space - */
const AU_DATE_RE = /^(\d{1,2})[/.\s-](\d{1,2})[/.\s-](\d{2}|\d{4})$/;
const COMPACT_8_RE = /^(\d{2})(\d{2})(\d{4})$/;
const COMPACT_6_RE = /^(\d{2})(\d{2})(\d{2})$/;

function twoDigits(value: number): string {
  return String(value).padStart(2, '0');
}

/**
 * Expand a two-digit year to four digits.
 *
 * Rule (deterministic, independent of "today"):
 *   00–69 → 2000–2069
 *   70–99 → 1970–1999
 *
 * Covers near-future roster dates (e.g. 26 → 2026) and living resident
 * birth decades without sliding-window non-determinism.
 */
export function expandTwoDigitYear(yy: number): number {
  if (yy < 0 || yy > 99 || !Number.isInteger(yy)) {
    throw new RangeError(`expandTwoDigitYear expects 0–99, got ${yy}`);
  }
  return yy < 70 ? 2000 + yy : 1900 + yy;
}

function validIsoDate(year: number, month: number, day: number): string | null {
  if (year < 1900 || year > 2100 || month < 1 || month > 12 || day < 1 || day > 31) {
    return null;
  }

  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }

  return `${year}-${twoDigits(month)}-${twoDigits(day)}`;
}

function yearFromToken(token: string): number {
  if (token.length === 2) return expandTwoDigitYear(Number(token));
  return Number(token);
}

/**
 * Parse free-text date entry to ISO `YYYY-MM-DD`.
 * Accepts ISO, AU `D/M/YYYY` or `D/M/YY` (with `/` `.` space `-`), and
 * compact `DDMMYYYY` / `DDMMYY`. Empty → `''`. Unparseable → `null`.
 */
export function parseIsoDateInput(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return '';

  const iso = ISO_DATE_RE.exec(trimmed);
  if (iso) {
    return validIsoDate(Number(iso[1]), Number(iso[2]), Number(iso[3]));
  }

  const au = AU_DATE_RE.exec(trimmed);
  if (au?.[1] && au[2] && au[3]) {
    return validIsoDate(yearFromToken(au[3]), Number(au[2]), Number(au[1]));
  }

  // Compact DDMMYYYY (pure digits or already partially separated — digit length wins).
  if (/^[\d/.\s-]+$/.test(trimmed)) {
    const digitsOnly = trimmed.replace(/\D/g, '');
    if (digitsOnly.length === 8) {
      const c8 = COMPACT_8_RE.exec(digitsOnly);
      if (c8) return validIsoDate(Number(c8[3]), Number(c8[2]), Number(c8[1]));
    }
    // Compact DDMMYY only when the raw value is pure digits (avoid stealing partials).
    if (/^\d{6}$/.test(trimmed)) {
      const c6 = COMPACT_6_RE.exec(trimmed);
      if (c6) {
        return validIsoDate(expandTwoDigitYear(Number(c6[3])), Number(c6[2]), Number(c6[1]));
      }
    }
  }

  return null;
}

/**
 * True when the text is long enough that a failed parse is unambiguous
 * (not mid-keystroke partial entry). Used to surface validation before blur.
 *
 * Two-digit years are intentionally NOT unambiguous: `03/08/20` is a normal
 * intermediate while typing `03/08/2026`, so we wait for blur to surface errors
 * or normalise shorthand.
 */
export function isUnambiguousAuDateText(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed) return false;
  // Complete AU with 4-digit year (DD/MM/YYYY family)
  if (/^\d{1,2}[/.\s-]\d{1,2}[/.\s-]\d{4}$/.test(trimmed)) return true;
  // Compact 8-digit pure digits
  if (/^\d{8}$/.test(trimmed)) return true;
  return false;
}

export function formatIsoDateForInput(value: string | null | undefined): string {
  const iso = ISO_DATE_RE.exec(String(value ?? ''));
  if (!iso) return '';
  return `${iso[3]}/${iso[2]}/${iso[1]}`;
}

export function isoDateRangeError(value: string, min?: string, max?: string): string | null {
  if (!value) return null;
  if (min && value < min) {
    return `Enter a date on or after ${formatIsoDateForInput(min)}.`;
  }
  if (max && value > max) {
    return `Enter a date on or before ${formatIsoDateForInput(max)}.`;
  }
  return null;
}

/**
 * Validation message for the date field.
 * Mid-type partials stay silent until blur (`touched`) or unambiguous length.
 */
export function dateEntryValidationMessage(options: {
  text: string;
  touched: boolean;
  min?: string;
  max?: string;
}): string {
  const { text, touched, min, max } = options;
  const trimmed = text.trim();
  if (!trimmed) return '';

  const parsed = parseIsoDateInput(trimmed);
  const unambiguous = isUnambiguousAuDateText(trimmed);
  const shouldSurface = touched || unambiguous;
  if (!shouldSurface) return '';

  if (parsed === null) return 'Enter a valid date.';
  if (parsed === '') return '';

  const rangeMessage = isoDateRangeError(parsed, min, max);
  return rangeMessage ?? '';
}

/**
 * Format partial numeric entry for mobile keyboards that do not expose '/'.
 * Pure-digit progressive entry auto-inserts slashes; user-typed shorthand
 * with variable-width parts is preserved for blur normalisation.
 */
export function formatPartialAuDateInput(value: string): string {
  const trimmed = value.trim();
  if (ISO_DATE_RE.test(trimmed)) return formatIsoDateForInput(trimmed);
  if (!/^[\d/.\s-]*$/.test(value)) return value;

  const digits = value.replace(/\D/g, '').slice(0, 8);

  // Complete 8-digit compact (with or without separators) → DD/MM/YYYY
  if (digits.length === 8) {
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  }

  // User-typed separated shorthand / partial (e.g. 3/8/26, 03/08/202)
  // — preserve structure so single-digit day/month and 2-digit years survive.
  if (/[/.\s-]/.test(trimmed)) {
    // Every INTERMEDIATE state of separated typing must match, not just the
    // finished shorthand. The previous pattern required a complete second
    // number, so "2/" — the state that exists the instant a coordinator types
    // the first slash — fell through to the digit-rebuild path below and had
    // its separator stripped. Typing "2/9/26" therefore degraded keystroke by
    // keystroke to "29/26": 2 → 2/ → back to 2 → 29 → 29/2 → 29/26. The day and
    // month were destroyed while she typed, the field went aria-invalid, no ISO
    // value was produced, and shift creation was blocked with the date box
    // showing something she had not typed.
    //
    // Trailing separators and empty groups are therefore allowed through
    // verbatim; `parseIsoDateInput` on blur remains the single authority on
    // whether the finished text is a real date.
    const flexible = /^(\d{1,2})(?:[/.\s-](\d{0,2})(?:[/.\s-](\d{0,4}))?)?$/.exec(trimmed);
    if (flexible) return trimmed;
    // e.g. 03/082 mid progressive after auto-slash — fall through to digit rebuild
  }

  // Pure digit progressive entry
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

/** Live commit only for complete 4-digit-year forms (avoid committing `03/08/20` as 2020 mid-type). */
export function canCommitLiveParse(text: string): boolean {
  const trimmed = text.trim();
  if (ISO_DATE_RE.test(trimmed)) return true;
  if (/^\d{1,2}[/.\s-]\d{1,2}[/.\s-]\d{4}$/.test(trimmed)) return true;
  if (/^\d{8}$/.test(trimmed.replace(/\D/g, '')) && trimmed.replace(/\D/g, '').length === 8) {
    // 8 digits complete — either pure or already formatted DD/MM/YYYY
    return /^\d{2}\/\d{2}\/\d{4}$/.test(trimmed) || /^\d{8}$/.test(trimmed);
  }
  return false;
}

export function addDaysIso(iso: string, delta: number): string {
  const match = ISO_DATE_RE.exec(iso);
  if (!match) return iso;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  date.setUTCDate(date.getUTCDate() + delta);
  return `${date.getUTCFullYear()}-${twoDigits(date.getUTCMonth() + 1)}-${twoDigits(date.getUTCDate())}`;
}

export function startOfMonthIso(iso: string): string {
  const match = ISO_DATE_RE.exec(iso);
  if (!match) return iso;
  return `${match[1]}-${match[2]}-01`;
}

export function addMonthsIso(iso: string, delta: number): string {
  const match = ISO_DATE_RE.exec(iso);
  if (!match) return iso;
  const year = Number(match[1]);
  const month = Number(match[2]); // 1-12
  const day = Number(match[3]);
  // Absolute 0-based month index (Y/M arithmetic only — not money).
  const absoluteMonthIndex = year * 12 + (month - 1) + delta;
  const nextYear = Math.floor(absoluteMonthIndex / 12);
  const nextMonth = (absoluteMonthIndex % 12) + 1;
  // Clamp day into the target month
  const padded = validIsoDate(nextYear, nextMonth, day);
  if (padded) return padded;
  // Last day of month fallback
  for (let d = Math.min(day, 31); d >= 1; d -= 1) {
    const candidate = validIsoDate(nextYear, nextMonth, d);
    if (candidate) return candidate;
  }
  return iso;
}

/**
 * Civil `YYYY-MM-DD` for an instant in an explicit IANA timezone.
 * Same algorithm as `@statura/utils` `dateOnlyInTz` — kept local so this
 * package stays free of a utils dependency. No clock read; caller supplies
 * the instant + zone.
 */
function isoDateInTimeZone(instant: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(instant);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

// Platform UI primitives have no tenant context. Match @statura/utils DEFAULT_TZ
// until callers can thread an explicit zone (additive prop, later).
// eslint-disable-next-line statura/no-hardcoded-tenant-timezone -- platform DEFAULT_TZ fallback for shared UI with no tenant context; same value as packages/utils/src/timezone.ts DEFAULT_TZ
const PLATFORM_DEFAULT_TZ = 'Australia/Sydney';

/**
 * Platform-default calendar "today" as `YYYY-MM-DD`.
 *
 * Resolves via Intl + explicit IANA zone so SSR (Vercel UTC) and the client
 * agree on the same civil date. Runtime-local `.getFullYear/.getMonth/.getDate`
 * would shift the initial calendar month by one day for AU users after UTC
 * midnight — the defect class this branch fixes.
 *
 * Prefer passing `timeZone` when the tenant zone is known. Name kept for
 * export stability (value is timezone civil date, not UTC).
 */
export function todayIsoUtc(timeZone: string = PLATFORM_DEFAULT_TZ): string {
  return isoDateInTimeZone(new Date(), timeZone);
}

/** Monday-first month grid cells (null = padding). */
export function buildMonthGrid(year: number, month: number): Array<string | null> {
  const first = new Date(Date.UTC(year, month - 1, 1));
  // getUTCDay: 0=Sun … 6=Sat → Monday-first offset
  const mondayFirst = (first.getUTCDay() + 6) % 7;
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const cells: Array<string | null> = [];
  for (let i = 0; i < mondayFirst; i += 1) cells.push(null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(`${year}-${twoDigits(month)}-${twoDigits(day)}`);
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export function dateCalendarViewportPosition(input: {
  trigger: { left: number; right: number; top: number; bottom: number };
  panelWidth: number;
  panelHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  margin?: number;
  gap?: number;
}): { top: number; left: number } {
  const margin = input.margin ?? 8;
  const gap = input.gap ?? 4;
  const panelWidth = Math.min(input.panelWidth, Math.max(0, input.viewportWidth - margin * 2));
  const panelHeight = Math.min(input.panelHeight, Math.max(0, input.viewportHeight - margin * 2));
  const left = Math.max(
    margin,
    Math.min(input.trigger.right - panelWidth, input.viewportWidth - panelWidth - margin),
  );
  const below = input.trigger.bottom + gap;
  const above = input.trigger.top - panelHeight - gap;
  const preferredTop =
    below + panelHeight <= input.viewportHeight - margin || above < margin ? below : above;
  const top = Math.max(margin, Math.min(preferredTop, input.viewportHeight - panelHeight - margin));
  return { top, left };
}

export { ISO_DATE_RE };
